import { FeatureItem, type FeatureType } from '../models/features/FeatureItem';
import { ProductSearchCombinationsByImageFeatureItem } from '../models/features/main/ProductSearchCombinationsByImageFeatureItem';
import { ProductSearchByTextFeatureItem } from '../models/features/main/ProductSearchByTextFeatureItem';
import { ProductSearchCombinationsFeatureItem } from '../models/features/main/ProductSearchCombinationsFeatureItem';
import { ProductSearchNewFeatureItem } from '../models/features/main/ProductSearchNewFeatureItem';
import { ProductSearchSimilarFeatureItem } from '../models/features/main/ProductSearchSimilarFeatureItem';
import { ProductSearchSimilarByImageFeatureItem } from '../models/features/main/ProductSearchSimilarByImageFeatureItem';
import { ProductSearchComplementaryCategoryFeatureItem } from '../models/features/main/ProductSearchComplementaryCategoryFeatureItem';
import type { ProductComparisonFeatureItem } from '../models/features/main/ProductComparisonFeatureItem';
import type { FilterableFeatureItem } from '../models/features/main/FilterableFeatureItem';
import type { WithSuggestionsFeatureItem } from '../models/features/main/WithSuggestionsFeatureItem';
import { BackableFeatureItem } from '../models/features/main/BackableFeatureItem';
import type { ProductDetailFeatureItem } from '../models/features/main/ProductDetailFeatureItem';
import type { FeatureHandler } from './features/FeatureHandler';
import type { AppDispatch, RootState } from '../store/store';
import { productDetailMainFeatureHandler } from './features/main/ProductDetailMainFeatureHandler';
import type { FeatureOptions } from '../api/models/FeatureOptions';
import { productComparisonMainFeatureHandler } from './features/main/ProductComparisonMainFeatureHandler';
import { productSearchByTextMainFeatureHandler } from './features/main/ProductSearchByTextMainFeatureHandler';
import { productSearchByImageMenuFeatureHandler } from './features/menu/ProductSearchByImageMenuFeatureHandler';
import { productSearchByTextMenuFeatureHandler } from './features/menu/ProductSearchByTextMenuFeatureHandler';
import { productMixAndMatchMainFeatureHandler } from './features/main/ProductMixAndMatchMainFeatureHandler';
import { productSearchCombinationsByImageMainFeatureHandler } from './features/main/ProductSearchCombinationsByImageMainFeatureHandler';
import { productSearchSimilarByImageMainFeatureHandler } from './features/main/ProductSearchSimilarByImageMainFeatureHandler';
import { productSearchSimilarMainFeatureHandler } from './features/main/ProductSearchSimilarMainFeatureHandler';
import { productSearchCombinationsMainFeatureHandler } from './features/main/ProductSearchCombinationsMainFeatureHandler';
import { productSearchComplementaryCategoryMainFeatureHandler } from './features/main/ProductSearchComplementaryCategoryMainFeatureHandler';
import { productSearchNewMainFeatureHandler } from './features/main/ProductSearchNewMainFeatureHandler';
import { productMultiVariantAddToCartMainFeatureHandler } from './features/main/ProductMultiVariantAddToCartMainFeatureHandler';
import { FilterableFeatureHandler } from './features/FilterableFeatureHandler';
import { BackableFeatureHandler } from './features/BackableFeatureHandler';
import { ProductMixAndMatchFeatureItem } from '../models/features/main/ProductMixAndMatchFeatureItem';
import { chatSidebarFeatureHandler } from './features/sidebar/ChatSidebarFeatureHandler';
import type { Feature } from '@public-api-contracts/models/chat/ChatMessage';

type AllFeatureHandlers = {
    [key in FeatureType]: FeatureHandler<
        Extract<FeatureItem, { featureType: key }>
    >;
};

type AllFilterableFeatureHandlers = {
    [key in Extract<
        FeatureItem,
        FilterableFeatureItem
    >['featureType']]: FilterableFeatureHandler;
};

type AllBackableFeatureHandlers = {
    [key in Extract<
        FeatureItem,
        BackableFeatureItem
    >['featureType']]: BackableFeatureHandler;
};

type ProductSearchListFeatureItem =
    | ProductSearchSimilarByImageFeatureItem
    | ProductSearchByTextFeatureItem
    | ProductSearchSimilarFeatureItem
    | ProductSearchNewFeatureItem
    | ProductSearchComplementaryCategoryFeatureItem;

type ProductSearchGroupsFeatureItem =
    | ProductSearchCombinationsByImageFeatureItem
    | ProductSearchCombinationsFeatureItem;

type WithSidebarFeatureItem =
    | ProductComparisonFeatureItem
    | ProductDetailFeatureItem;

const featureHandlers: AllFeatureHandlers = {
    'product-detail': productDetailMainFeatureHandler,
    'product-comparison': productComparisonMainFeatureHandler,
    'product-search-by-text': productSearchByTextMainFeatureHandler,
    chat: chatSidebarFeatureHandler,
    'product-search-by-image-menu': productSearchByImageMenuFeatureHandler,
    'product-search-by-text-menu': productSearchByTextMenuFeatureHandler,
    'product-mix-and-match': productMixAndMatchMainFeatureHandler,
    'product-search-combinations-by-image':
        productSearchCombinationsByImageMainFeatureHandler,
    'product-search-combinations': productSearchCombinationsMainFeatureHandler,
    'product-search-similar-by-image':
        productSearchSimilarByImageMainFeatureHandler,
    'product-search-similar': productSearchSimilarMainFeatureHandler,
    'product-search-complementary-category':
        productSearchComplementaryCategoryMainFeatureHandler,
    'product-search-new': productSearchNewMainFeatureHandler,
    'product-multi-variant-add-to-cart':
        productMultiVariantAddToCartMainFeatureHandler
};

const filterableFeatureHandlers = Object.fromEntries(
    Object.entries(featureHandlers).filter(
        ([, handler]) => handler.isFilterable === true
    )
) as unknown as AllFilterableFeatureHandlers;

const backableFeatureHandlers = Object.fromEntries(
    Object.entries(featureHandlers).filter(
        ([, handler]) => handler.isBackable === true
    )
) as unknown as AllBackableFeatureHandlers;

export function startFeatureItem<
    Opts extends FeatureOptions,
    T extends Opts['featureType'],
    R extends Extract<FeatureItem, { featureType: T }>,
    H extends FeatureHandler<R>
>(
    dispatch: AppDispatch,
    state: RootState,
    featureOptions: Opts
): Promise<void> {
    const handler = featureHandlers[featureOptions.featureType] as H;
    return handler.startFeatureItem(
        dispatch,
        state,
        featureOptions as unknown as Extract<
            FeatureOptions,
            { featureType: R['featureType'] }
        >
    );
}

export function restoreFeatureItem<
    F extends Feature,
    T extends F['featureType'],
    R extends Extract<FeatureItem, { featureType: T }>,
    H extends FeatureHandler<R>
>(dispatch: AppDispatch, state: RootState, feature: F): void {
    const handler = featureHandlers[feature.featureType] as H;
    handler.restoreFeatureItem?.(
        dispatch,
        state,
        feature as unknown as Extract<
            Feature,
            { featureType: R['featureType'] }
        >
    );
}

export function applyFilters(
    dispatch: AppDispatch,
    state: RootState,
    appliedFilters: Partial<FilterableFeatureItem>
): Promise<void> {
    const featureItem = state.featureState.mainFeatureItem;
    if (!isFilterableFeatureItem(featureItem)) {
        return Promise.resolve();
    }
    const handler = filterableFeatureHandlers[featureItem.featureType];
    return handler.applyFilters(dispatch, state, appliedFilters);
}

export function backToPreviousFeatureItems(
    dispatch: AppDispatch,
    state: RootState
): void {
    const featureItem = state.featureState.mainFeatureItem;
    if (!isBackableFeatureItem(featureItem)) {
        return;
    }
    const handler = backableFeatureHandlers[featureItem.featureType];
    handler.backToPreviousFeatureItems(dispatch, state);
}

export function isFilterableFeatureItem(
    featureItem: FeatureItem | null
): featureItem is Extract<FeatureItem, FilterableFeatureItem> {
    if (!featureItem) {
        return false;
    }
    return featureHandlers[featureItem.featureType].isFilterable === true;
}
// Right now, this is the same check because all product list feature items are filterable.
export function isProductSearchListFeatureItem(
    featureItem: FeatureItem | null
): featureItem is ProductSearchListFeatureItem {
    return (
        !!featureItem &&
        (featureItem.featureType === 'product-search-similar-by-image' ||
            featureItem.featureType === 'product-search-by-text' ||
            featureItem.featureType === 'product-search-similar' ||
            featureItem.featureType === 'product-search-new' ||
            featureItem.featureType === 'product-search-complementary-category')
    );
}
export function isProductSearchGroupsFeatureItem(
    featureItem: FeatureItem | null
): featureItem is ProductSearchGroupsFeatureItem {
    return (
        !!featureItem &&
        (featureItem.featureType === 'product-search-combinations-by-image' ||
            featureItem.featureType === 'product-search-combinations')
    );
}

export function isProductComparisonFeatureItem(
    featureItem: FeatureItem | null
): featureItem is ProductComparisonFeatureItem {
    return !!featureItem && featureItem.featureType === 'product-comparison';
}

export function isBackableFeatureItem(
    featureItem: FeatureItem | null
): featureItem is Extract<FeatureItem, BackableFeatureItem> {
    return (
        !!featureItem &&
        (featureItem.featureType === 'product-detail' ||
            featureItem.featureType === 'product-comparison' ||
            featureItem.featureType ===
                'product-search-complementary-category' ||
            featureItem.featureType === 'product-multi-variant-add-to-cart')
    );
}

export function isWithSidebarFeatureItem(
    featureItem: FeatureItem | null
): featureItem is WithSidebarFeatureItem {
    return (
        !!featureItem &&
        (featureItem.featureType === 'product-comparison' ||
            featureItem.featureType === 'product-detail')
    );
}

function isOriginFeatureItem(
    featureItem: FeatureItem | null
): featureItem is
    | ProductSearchListFeatureItem
    | ProductSearchGroupsFeatureItem
    | ProductComparisonFeatureItem
    | ProductMixAndMatchFeatureItem {
    return (
        !!featureItem &&
        (isProductSearchListFeatureItem(featureItem) ||
            isProductSearchGroupsFeatureItem(featureItem) ||
            isProductComparisonFeatureItem(featureItem) ||
            featureItem.featureType === 'product-mix-and-match')
    );
}

export function getDefaultFilterableFeatureItem(): FilterableFeatureItem {
    return {
        availableFieldFilters: [],
        availableCategoryFilters: [],
        availableMaxPrice: null,
        availableMinPrice: null,
        appliedFieldFilters: [],
        appliedCategoryFilters: [],
        appliedMinPrice: null,
        appliedMaxPrice: null,
        appliedSortBy: 'relevance',
        refinementLoadingStatus: 'idle'
    };
}

export function getDefaultWithSuggestionsFeatureItem(): WithSuggestionsFeatureItem {
    return {
        suggestedProducts: [],
        suggestedFieldFilters: [],
        suggestedCategoryFilters: []
    };
}

export function getDefaultBackableFeatureItem(
    state: RootState
): BackableFeatureItem {
    if (
        state.uiState.mode === 'full' &&
        isOriginFeatureItem(state.featureState.mainFeatureItem)
    ) {
        return {
            previousMainFeatureItem: state.featureState.mainFeatureItem,
            previousSidebarFeatureItem: state.featureState.sidebarFeatureItem
        };
    }
    return {
        previousMainFeatureItem: null,
        previousSidebarFeatureItem: null
    };
}

export function defaultBackToPreviousFeatureItems(
    dispatch: AppDispatch,
    state: RootState
): void {
    if (!isBackableFeatureItem(state.featureState.mainFeatureItem)) {
        return;
    }
    const previousMainFeatureItem =
        state.featureState.mainFeatureItem.previousMainFeatureItem;
    if (!previousMainFeatureItem) {
        return;
    }
    const previousSidebarFeatureItem =
        state.featureState.mainFeatureItem.previousSidebarFeatureItem;
    dispatch.featureState.setMenuFeatureItem(null);
    dispatch.featureState.setMainFeatureItem(previousMainFeatureItem);
    dispatch.featureState.setSidebarFeatureItem(previousSidebarFeatureItem);
}

export function getNewFeatureUuid(featureType: FeatureType): string {
    return `${Date.now()}-${featureType}`;
}
